import { useEffect } from 'react';

import { useGetMeQuery } from '../api/userApi';
import { writeIdentityHint } from '../services/identityHint';
import { hasSessionHint } from '../services/sessionPresence';
import { useTypedSelector } from '../store';

import type { User } from '../api/types';
import type { RootState } from '../store';

export const useMe = () => {
	const accessToken = useTypedSelector(
		(state: RootState) => state.authState.accessToken,
	);
	// Probing /me without a session hint guarantees a 401, which the refresh
	// path then mistakes for an expired session — that's the root cause of the
	// spurious "Session expired" toast on a freshly-loaded /signin. Skip the
	// probe when there's no reason to believe a session exists. Web auth uses
	// the session-presence hint because its refresh credential is cookie-backed.
	const skip = !accessToken && !hasSessionHint();
	const { data, isLoading, isError, refetch } = useGetMeQuery(undefined, {
		skip,
	});
	const userFromState = useTypedSelector(
		(state: RootState) => state.authState.user,
	);

	// Combine data from query and state
	const user = data || userFromState;

	// Refresh the identity hint that powers the WelcomeBackScreen account
	// chooser. Writing only on successful /me resolution keeps the hint
	// authoritative (server source of truth) while the cleanup of the hint
	// is owned by `markSessionEnded()` — see authSlice. The chooser stays
	// useful across cookie expiries / fresh installs because the hint
	// persists in localStorage until logout / refresh-fail.
	useEffect(() => {
		if (!data) return;
		writeIdentityHint({
			name: data.name ?? null,
			email: data.email,
			avatarUrl: data.profile_pic ?? null,
			orgName: null,
			provider: data.provider ?? null,
			lastSignedInAt: new Date().toISOString(),
		});
	}, [data]);

	return {
		data: user as User | undefined,
		isLoading,
		isError,
		refetch,
	};
};
